import { useEffect, useState } from "react";
import { useContent } from "../content/ContentContext";
import API, { apiErrorMessage } from "../lib/api";

const inputCls =
  "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink placeholder:text-muted/60 transition-colors focus:border-accent";

export default function EnquiryForm({ preselect = "", idPrefix = "cf" }) {
  const { content } = useContent();
  const { services, packages } = content;
  const options = [...new Set([
    ...services.filter((s) => !s.hidden).map((s) => s.name),
    ...packages.filter((p) => !p.hidden).map((p) => p.name),
    "Multiple services",
    "I'm not sure yet",
  ])];
  const [form, setForm] = useState({
    name: "", email: "", company: "",
    service: options.includes(preselect) ? preselect : "",
    message: "", budget: "", start_date: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (preselect && options.includes(preselect)) setForm((f) => ({ ...f, service: preselect }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselect]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const id = (k) => `${idPrefix}-${k}`;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await API.post("/enquiries", form);
      if (res.status === 200 || res.status === 201) setSubmitted(true);
      else setError("Your enquiry could not be submitted. Please try again or email me directly.");
    } catch (err) {
      setError(apiErrorMessage(err, "Your enquiry could not be submitted. Please try again or email me directly."));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="contact-success-message" className="rounded-2xl border border-line bg-white p-10 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-lavender font-serif text-2xl font-bold text-plum">
          ss<span className="text-accent">.</span>
        </span>
        <h2 className="mt-6 font-serif text-2xl font-medium text-ink">
          Thank you, {form.name.split(" ")[0] || "there"}. Your enquiry has been received.
        </h2>
        <p className="mx-auto mt-4 max-w-md leading-relaxed text-muted">
          It has arrived in my private inbox. I&rsquo;ll reply to{" "}
          <span className="font-medium text-ink">{form.email}</span> with next
          steps — usually a short set of questions or a proposed scope.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="enquiry-form" onSubmit={submit} className="rounded-2xl border border-line bg-white p-8 sm:p-10">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor={id("name")} className="mb-2 block text-sm font-medium text-ink">
            Name <span aria-hidden="true" className="text-accent">*</span>
          </label>
          <input id={id("name")} data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} autoComplete="name" />
        </div>
        <div>
          <label htmlFor={id("email")} className="mb-2 block text-sm font-medium text-ink">
            Email <span aria-hidden="true" className="text-accent">*</span>
          </label>
          <input id={id("email")} data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} autoComplete="email" />
        </div>
        <div>
          <label htmlFor={id("company")} className="mb-2 block text-sm font-medium text-ink">
            Company name <span className="text-muted">(optional)</span>
          </label>
          <input id={id("company")} data-testid="contact-company-input" value={form.company} onChange={set("company")} className={inputCls} autoComplete="organization" />
        </div>
        <div>
          <label htmlFor={id("service")} className="mb-2 block text-sm font-medium text-ink">
            Service or package <span aria-hidden="true" className="text-accent">*</span>
          </label>
          <select id={id("service")} data-testid="contact-service-select" required value={form.service} onChange={set("service")} className={inputCls}>
            <option value="" disabled>Select…</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={id("budget")} className="mb-2 block text-sm font-medium text-ink">
            Budget range <span className="text-muted">(optional)</span>
          </label>
          <input id={id("budget")} data-testid="contact-budget-input" value={form.budget} onChange={set("budget")} placeholder="e.g. €500 – €1,000" className={inputCls} />
        </div>
        <div>
          <label htmlFor={id("start")} className="mb-2 block text-sm font-medium text-ink">
            Preferred start date <span className="text-muted">(optional)</span>
          </label>
          <input id={id("start")} data-testid="contact-start-input" value={form.start_date} onChange={set("start_date")} placeholder="e.g. Early March" className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor={id("message")} className="mb-2 block text-sm font-medium text-ink">
            Project description <span aria-hidden="true" className="text-accent">*</span>
          </label>
          <textarea id={id("message")} data-testid="contact-message-input" required minLength={10} rows={5} value={form.message} onChange={set("message")}
            placeholder="A few sentences about your situation, the data or process involved, and what would make this a success."
            className={inputCls} />
        </div>
      </div>
      {error && (
        <p data-testid="contact-error-message" role="alert" className="mt-5 rounded-xl border border-accent bg-lavender px-4 py-3 text-sm text-plum">
          {error}
        </p>
      )}
      <div className="mt-8 flex flex-wrap items-center gap-5">
        <button data-testid="contact-submit-button" type="submit" disabled={submitting}
          className="inline-flex items-center rounded-full bg-plum px-8 py-3 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-ink disabled:cursor-not-allowed disabled:opacity-60">
          {submitting ? "Sending…" : "Send enquiry"}
        </button>
        <p className="text-xs text-muted">Your enquiry is sent only to me and is never published.</p>
      </div>
    </form>
  );
}
